import { useState, useCallback } from 'react';
import { paymentActions } from '../../../_actions';
import { paymentService } from '../../../_services/payment.service';
import {
  PAYMENT_TYPES,
  buildServicesToBook,
  effectiveUnitPriceAfterCartDiscount,
  subtotalAfterItemDiscounts,
} from '../utils/checkoutCalculations';
import toast from '../../shared/Toast';
import { paymentStartError } from '../utils/paymentStartError';

// The stylist on the booking, named when the server refuses a card payment.
const staffNameFor = (state) => (state.lineItems || []).find(i => i.type === 'service')?.staffName
  || state.booking?.hairdresser?.name || '';

/**
 * Hook that provides payment processing functions for each payment method.
 * Each method returns a promise that resolves when the payment is complete.
 */
export default function usePaymentMethods({ state, actions, dispatch, syncBookingToBackend }) {

  const [paymentLinkData, setPaymentLinkData] = useState(null);
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [paymentLinkType, setPaymentLinkType] = useState(null); // 'payment' | 'klarna' | 'clearpay'
  // A deposit payment-link COLLECTS A DEPOSIT (booking stays open), unlike a
  // balance link which completes the booking. These flag the active link as a
  // deposit so completion records a deposit instead of marking checkout complete.
  const [paymentLinkIsDeposit, setPaymentLinkIsDeposit] = useState(false);
  const [paymentLinkDepositAmount, setPaymentLinkDepositAmount] = useState(null);
  const [cardReaderList, setCardReaderList] = useState([]);
  const [paymentMethodInfo, setPaymentMethodInfo] = useState(null); // Card on file info

  // Build common params for payment APIs.
  // Cart discount is spread proportionally into both servicesToBook[].price and
  // productIds[].retailPrice so the backend's commission calc reflects the
  // actual sold-at price for each item type at its own commission rate.
  const buildPaymentParams = useCallback((amount) => {
    const denom = subtotalAfterItemDiscounts(state.lineItems);
    const servicesToBook = buildServicesToBook(state.lineItems, state.cartDiscount);
    const productIds = [];
    state.lineItems.filter(item => item.type === 'product').forEach(item => {
      const qty = item.quantity || 1;
      const retailPrice = effectiveUnitPriceAfterCartDiscount(item, state.cartDiscount, denom);
      for (let i = 0; i < qty; i++) {
        // Send the real catalogue id (_id) + stock/commission flag so the backend
        // can link stock and compute the stylist's product commission. (item.id is
        // a synthetic cart id — don't send it as the product id.)
        // NB this path expands a qty-N line into N entries of one, so it must
        // NOT also send a quantity — that would multiply twice. The POS sends
        // a single entry carrying its quantity instead. Both end up correct.
        productIds.push({ _id: item.serviceId, retailPrice, stockPrice: item.stockPrice || 0, commissionEnable: item.commissionEnable || 0, variantId: item.variantId || undefined });
      }
    });
    const voucherIds = state.transactions
      .filter(t => t.paymentType === PAYMENT_TYPES.VOUCHER)
      .map(t => ({ id: t.voucherId, value: t.amount, code: t.voucherCode }));

    return {
      bookingId: state.bookingId,
      servicesToBook,
      productIds,
      voucherIds,
      amount,
    };
  }, [state.bookingId, state.lineItems, state.transactions, state.cartDiscount]);

  // Load card reader list + card on file info.
  // bookingId is required for the backend to look up the booking's
  // stripePaymentMethodId — without it, card-on-file stays hidden.
  const loadCardReaders = useCallback(async (bookingId) => {
    try {
      const response = await dispatch(paymentActions.cardReaderList(bookingId ? { bookingId } : undefined));
      const result = response?.data?.responseData?.result || {};
      // Stripe terminal.readers.list() returns readers under .data
      setCardReaderList(result.data || []);
      setPaymentMethodInfo(result.paymentMethodInfo || null);
    } catch (e) {
      // silently fail
    }
  }, [dispatch]);

  // --- SIMPLE METHODS (Cash, Card, Other) ---
  // These just record the transaction locally. The actual API call happens on checkout completion.
  const processSimplePayment = useCallback((type, amount, label) => {
    actions.addTransaction({ paymentType: type, amount, label });
  }, [actions]);

  // --- PAYMENT LINK (Type 12) ---
  const processPaymentLink = useCallback(async (amount) => {
    actions.setProcessing(true);
    try {
      const params = buildPaymentParams(amount);
      const response = await dispatch(paymentActions.createPaymentLink(params));
      if (response?.data?.statusCode === 1) {
        const linkData = response.data.responseData;
        setPaymentLinkData(linkData);
        setPaymentLinkType('payment');
        actions.setScreen('paymentLink');
      } else {
        toast.error('Failed to create payment link. Please try again.');
      }
    } catch (e) {
      toast.error('Failed to create payment link');
    }
    actions.setProcessing(false);
  }, [actions, buildPaymentParams, dispatch]);

  // --- KLARNA (Type 13) ---
  const processKlarna = useCallback(async (amount) => {
    actions.setProcessing(true);
    try {
      const params = buildPaymentParams(amount);
      const response = await dispatch(paymentActions.createKlarnaPaymentLink(params));
      if (response?.data?.statusCode === 1) {
        const linkData = response.data.responseData;
        setPaymentLinkData(linkData);
        setPaymentLinkType('klarna');
        actions.setScreen('paymentLink');
      } else {
        toast.error('Failed to create Klarna link. Please try again.');
      }
    } catch (e) {
      toast.error('Failed to create Klarna link');
    }
    actions.setProcessing(false);
  }, [actions, buildPaymentParams, dispatch]);

  // --- CLEARPAY (Type 14) ---
  const processClearpay = useCallback(async (amount) => {
    actions.setProcessing(true);
    try {
      const params = buildPaymentParams(amount);
      const response = await dispatch(paymentActions.createClearpayPaymentLink(params));
      if (response?.data?.statusCode === 1) {
        const linkData = response.data.responseData;
        setPaymentLinkData(linkData);
        setPaymentLinkType('clearpay');
        actions.setScreen('paymentLink');
      } else {
        toast.error('Failed to create Clearpay link. Please try again.');
      }
    } catch (e) {
      toast.error('Failed to create Clearpay link');
    }
    actions.setProcessing(false);
  }, [actions, buildPaymentParams, dispatch]);

  // --- CARD ON FILE (Type 10) ---
  const processCardOnFile = useCallback(async (amount) => {
    actions.setProcessing(true);
    try {
      // Save current items to backend before creating payment intent
      if (syncBookingToBackend) await syncBookingToBackend();

      const params = {
        ...buildPaymentParams(amount),
        selectedPaymentType: 10,
      };
      const response = await dispatch(paymentActions.paymentIntents(params));
      if (response?.data?.statusCode === 1) {
        const paymentIntentId = response.data.responseData?.result?.id;
        if (paymentIntentId) {
          const captureResponse = await dispatch(paymentActions.paymentIntentsCapture({
            paymentIntentId,
            bookingId: state.bookingId,
            servicesToBook: buildServicesToBook(state.lineItems, state.cartDiscount),
            voucherIds: params.voucherIds,
            selectedPaymentType: 10,
          }));
          if (captureResponse?.data?.statusCode === 1) {
            actions.addTransaction({ paymentType: PAYMENT_TYPES.CARD_ON_FILE, amount, label: 'Card on File' });
            toast.success('Payment captured');
            // Payment already captured electronically — auto-complete
            setTimeout(() => actions.markComplete(), 100);
          } else {
            toast.error('Failed to capture payment. Please try again.');
          }
        }
      } else {
        toast.error(paymentStartError(response, staffNameFor(state)));
      }
    } catch (e) {
      toast.error(paymentStartError(e, staffNameFor(state)));
    }
    actions.setProcessing(false);
  }, [actions, buildPaymentParams, dispatch, state.bookingId, state.lineItems]);

  // --- PAYMENT LINK COMPLETION (called from modal) ---
  const checkPaymentLinkStatus = useCallback(async () => {
    try {
      const response = await paymentService.checkPaymentLinkStatus({ bookingId: state.bookingId });
      return response?.data?.responseData || response?.data?.data || {};
    } catch (e) {
      return {};
    }
  }, [state.bookingId]);

  const handlePaymentLinkComplete = useCallback((booking) => {
    if (paymentLinkIsDeposit) {
      // Deposit link paid: record a DEPOSIT and keep the booking OPEN. A deposit
      // link collects a deposit — it must not complete the booking.
      const depositAmt = paymentLinkDepositAmount || paymentLinkData?.amount || 0;
      if (actions.addExistingTransaction) {
        actions.addExistingTransaction({
          paymentType: PAYMENT_TYPES.PAYMENT_LINK,
          amount: depositAmt,
          label: 'Deposit',
          isDeposit: true,
          createdAt: new Date().toISOString(),
        });
      }
      setPaymentLinkData(null);
      setPaymentLinkIsDeposit(false);
      setPaymentLinkDepositAmount(null);
      actions.setScreen('summary');
      return;
    }
    const amount = paymentLinkData?.amount || booking?.totalAmount || state.balanceRemaining;
    const typeMap = { payment: PAYMENT_TYPES.PAYMENT_LINK, klarna: PAYMENT_TYPES.KLARNA, clearpay: PAYMENT_TYPES.CLEARPAY };
    const labelMap = { payment: 'Payment Link', klarna: 'Klarna', clearpay: 'Clearpay' };
    actions.addTransaction({
      paymentType: typeMap[paymentLinkType] || PAYMENT_TYPES.PAYMENT_LINK,
      amount,
      label: labelMap[paymentLinkType] || 'Payment Link',
    });
    setPaymentLinkData(null);
    // Payment is already captured electronically — auto-complete the checkout
    // Small delay to let the transaction state update propagate
    setTimeout(() => {
      actions.markComplete();
    }, 100);
  }, [actions, paymentLinkData, paymentLinkType, state.balanceRemaining, paymentLinkIsDeposit, paymentLinkDepositAmount]);

  const closePaymentLinkModal = useCallback(() => {
    setPaymentLinkData(null);
    setPaymentLinkIsDeposit(false);
    setPaymentLinkDepositAmount(null);
    actions.setScreen('payment');
  }, [actions]);

  // Show an already-created deposit link (built by DepositScreen) in the
  // PaymentLinkScreen so the salon can copy/share it. Flagged as a deposit so
  // when paid it records a deposit and keeps the booking open.
  const showDepositPaymentLink = useCallback((linkData, depositAmount) => {
    setPaymentLinkData(linkData);
    setPaymentLinkType('payment');
    setPaymentLinkIsDeposit(true);
    setPaymentLinkDepositAmount(depositAmount);
    actions.setScreen('paymentLink');
  }, [actions]);

  return {
    // State
    paymentLinkData,
    showPaymentLinkModal,
    paymentLinkType,
    cardReaderList,
    paymentMethodInfo,

    // Methods
    buildPaymentParams,
    loadCardReaders,
    processSimplePayment,
    processPaymentLink,
    processKlarna,
    processClearpay,
    processCardOnFile,
    checkPaymentLinkStatus,
    handlePaymentLinkComplete,
    closePaymentLinkModal,
    showDepositPaymentLink,
  };
}
